import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  adminSettingsUpdate,
  getAdminSettings,
  timeZoneList,
} from "./services";
import { toast,ToastContainer } from "react-toastify";

const Settings = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeZones, setTimeZones] = useState([]);
  const [selectedTimeZone, setSelectedTimeZone] = useState("Asia/Kolkata");
  const [settings, setSettings] = useState({
    platform_name: "",
    support_email: "",
    time_zone: "",
    smtp_host: "",
    smtp_port: "",
    encryption: "",
    smtp_username: "",
    smtp_password: "",
    twoFactorAuth: false,
    strongPasswords: false,
    sessionTimeout: "",
    emailNewUser: false,
    emailFailedPayment: false,
    systemAlerts: false,
    backupFrequency: "",
    retentionPeriod: "",
    automaticBackups: false,
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getAdminSettings();
        const apiPayload = response.data.payload;
        setSettings({
          ...apiPayload,
          twoFactorAuth: apiPayload.enable_two_factor_auth,
          strongPasswords: apiPayload.require_strong_passwords,
          sessionTimeout: apiPayload.session_timeout,
          emailNewUser: apiPayload.email_new_users,
          emailFailedPayment: apiPayload.email_failed_payments,
          systemAlerts: apiPayload.system_alerts,
          automaticBackups: apiPayload.enable_auto_backup,
          backupFrequency: apiPayload.backup_frequency,
          retentionPeriod: apiPayload.retention_period,
        });
      } catch (error) {
        console.error("Failed to fetch admin settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    const updatePayload = {
      platform_name: settings.platform_name,
      support_email: settings.support_email,
      time_zone: selectedTimeZone,
      smtp_host: settings.smtp_host,
      smtp_port: parseInt(settings.smtp_port, 10),
      encryption: settings.encryption,
      smtp_username: settings.smtp_username,
      smtp_password: settings.smtp_password,
      enable_two_factor_auth: settings.twoFactorAuth,
      require_strong_passwords: settings.strongPasswords,
      session_timeout: parseInt(settings.sessionTimeout, 10),
      email_new_users: settings.emailNewUser,
      email_failed_payments: settings.emailFailedPayment,
      system_alerts: settings.systemAlerts,
      backup_frequency: settings.backupFrequency,
      retention_period: parseInt(settings.retentionPeriod, 10),
      enable_auto_backup: settings.automaticBackups,
    };

    try {
      const response = await adminSettingsUpdate(updatePayload);
      toast.success(response.data.message,{toastId:"update-success"});
      console.log("Settings updated successfully:", response);
    } catch (error) {
      toast.error(error.response.data.message,{toastId:"update-error"});
      console.error("Failed to update admin settings:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getTimeZoneList();
    setSelectedTimeZone(settings.time_zone)
  }, [settings.time_zone]);

  const getTimeZoneList = async () => {
    try {
      const response = await timeZoneList();
      if (response.status === 200) {
        setTimeZones(response.data.payload);
      }
    } catch (error) {
      console.error("Failed to fetch time zone list:", error);
    }
  };
  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
           
      <Box
        sx={{
          background: "#ffffff",
          padding: "30px",
          border: "1px solid #1570EF",
          borderRadius: "8px",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "24px", md: "36px", sm: "32px" },
              fontWeight: 600,
            }}
          >
            Settings
          </Typography>
          <Box>
            <button
              className="api-integration-btn"
              onClick={handleSave}
              disabled={loading} // Disable the button while loading
            >
              {loading ? "Saving..." : "Save All Changes"}
            </button>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 800 }}>
          {/* General Settings */}
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Platform Name"
                fullWidth
                name="platform_name"
                value={settings.platform_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Support Email"
                fullWidth
                name="support_email"
                value={settings.support_email}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">Time Zone</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedTimeZone}
                  label="Time Zone" // ✅ add this line
                  onChange={(event) => setSelectedTimeZone(event.target.value)}
                >
                  {timeZones.map((zone, index) => (
                    <MenuItem key={index} value={zone}>
                      {zone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Email Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Email Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="SMTP Host"
                fullWidth
                name="smtp_host"
                value={settings.smtp_host}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="SMTP Port"
                fullWidth
                name="smtp_port"
                value={settings.smtp_port}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Encryption"
                fullWidth
                name="encryption"
                value={settings.encryption}
                onChange={handleChange}
              >
                <MenuItem value="TLS">TLS</MenuItem>
                <MenuItem value="SSL">SSL</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="SMTP Username"
                fullWidth
                name="smtp_username"
                value={settings.smtp_username}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="outlined-adornment-password">
                  SMTP Password
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  name="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={settings.smtp_password}
                  onChange={handleChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="SMTP Password"
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* Security Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Security Settings
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.twoFactorAuth}
                  onChange={handleChange}
                  name="twoFactorAuth"
                />
              }
              label="Enable Two-Factor Authentication"
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.strongPasswords}
                  onChange={handleChange}
                  name="strongPasswords"
                />
              }
              label="Require Strong Passwords"
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
              <TextField
                label="Session Timeout (Minutes)"
                fullWidth
                name="sessionTimeout"
                value={settings.sessionTimeout}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Notification Settings
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.emailNewUser}
                  onChange={handleChange}
                  name="emailNewUser"
                />
              }
              label="Email Notification For New Users"
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.emailFailedPayment}
                  onChange={handleChange}
                  name="emailFailedPayment"
                />
              }
              label="Email Notification For Failed Payments"
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.systemAlerts}
                  onChange={handleChange}
                  name="systemAlerts"
                />
              }
              label="System Alert Notifications"
            />
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Backup Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Backup Frequency"
                fullWidth
                name="backupFrequency"
                value={settings.backupFrequency}
                onChange={handleChange}
              >
                <MenuItem value="Daily">Daily</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Retention Period(Days)"
                fullWidth
                name="retentionPeriod"
                value={settings.retentionPeriod}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            className="mt-2"
            control={
              <Checkbox
                checked={settings.automaticBackups}
                onChange={handleChange}
                name="automaticBackups"
              />
            }
            label="Enable Automatic Backups"
          />
          <br />
          <button
            className="export-report-btn mt-3"
            style={{ padding: "10px", fontWeight: 600 }}
          >
            Run Backup Now
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
